import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ProjectBreadcrumb } from '@/components/navigation/project-breadcrumb';
import { ApiError, apiClient } from '@/modules/api/api-client';
import { FREQUENT_STALE_TIME, INFREQUENT_STALE_TIME } from '@/modules/query/query-client';
import { queryKeys } from '@/modules/query/query-keys';
import { formatRelativeTime } from '@/utils/format-time';
import { truncateTitle } from '@/utils/truncate';

/** Repository detail from GET /repos/:name */
interface RepositoryDetail {
  name: string;
  arn: string;
  description?: string;
  defaultBranch?: string;
  cloneUrlHttp?: string;
  cloneUrlSsh?: string;
  createdAt: string;
  lastModifiedAt: string;
}

/** Branch info from GET /repos/:name/branches */
interface BranchInfo {
  name: string;
  commitId: string;
}

/** PR summary from GET /repos/:name/prs */
interface PrSummary {
  prId: string;
  title: string;
  authorId: string;
  authorDisplayName?: string;
  sourceBranch: string;
  destinationBranch: string;
  status: 'OPEN' | 'CLOSED' | 'MERGED';
  createdAt: string;
  lastActivityDate: string;
  approvalCount?: number;
  ciStatus?: 'PENDING' | 'RUNNING' | 'PASSED' | 'FAILED';
}

/** Paginated response from backend */
interface PaginatedResponse<T> {
  items: T[];
  cursor?: string;
}

type StatusFilter = 'open' | 'closed' | 'all';

const PR_PAGE_SIZE = 20;
const PR_TITLE_MAX = 120;

function StatusBadge({ status }: { status: 'OPEN' | 'CLOSED' | 'MERGED' }) {
  const styles = {
    OPEN: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    CLOSED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    MERGED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}
      aria-label={`Status: ${status.toLowerCase()}`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function PrEntry({ pr, repoName, projectKey }: { pr: PrSummary; repoName: string; projectKey?: string }) {
  return (
    <li>
      <Link
        to={projectKey ? `/projects/${encodeURIComponent(projectKey)}/repos/${encodeURIComponent(repoName)}/prs/${pr.prId}` : `/repos/${encodeURIComponent(repoName)}/prs/${pr.prId}`}
        className="block rounded-lg border border-gray-200 p-4 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {truncateTitle(pr.title, PR_TITLE_MAX)}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{pr.authorDisplayName ?? pr.authorId}</span>
              <span aria-label={`Created ${formatRelativeTime(pr.createdAt)}`}>
                {formatRelativeTime(pr.createdAt)}
              </span>
              {pr.approvalCount != null && pr.approvalCount > 0 && (
                <span aria-label={`${pr.approvalCount} approval${pr.approvalCount === 1 ? '' : 's'}`}>
                  👍 {pr.approvalCount}
                </span>
              )}
            </div>
          </div>
          <StatusBadge status={pr.status} />
        </div>
      </Link>
    </li>
  );
}

function RepoInfoSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading repository details" className="animate-pulse">
      <div className="h-8 w-64 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-3 h-4 w-96 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-4 flex gap-3">
        <div className="h-6 w-20 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-6 w-24 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    </div>
  );
}

function PrListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading pull requests" className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-lg border border-gray-200 p-4 dark:border-gray-700"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="mt-2 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="h-5 w-14 rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function RepoDetailPage() {
  const { name, projectKey, repoName: repoNameParam } = useParams<{
    name: string;
    projectKey: string;
    repoName: string;
  }>();
  // Support both /repos/:name and /projects/:projectKey/repos/:repoName routes
  const repoName = repoNameParam ?? name ?? '';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [cursors, setCursors] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);

  const currentCursor = currentPage > 0 ? cursors[currentPage - 1] : undefined;

  // Fetch repository details
  const {
    data: repo,
    isLoading: repoLoading,
    isError: repoError,
    error: repoErrorObj,
  } = useQuery({
    queryKey: queryKeys.repos.detail(repoName),
    queryFn: () => apiClient.get<RepositoryDetail>(`/repos/${encodeURIComponent(repoName)}`),
    staleTime: INFREQUENT_STALE_TIME,
    enabled: !!repoName,
  });

  // Fetch branches
  const { data: branches } = useQuery({
    queryKey: queryKeys.repos.branches(repoName),
    queryFn: () =>
      apiClient.get<PaginatedResponse<BranchInfo>>(
        `/repos/${encodeURIComponent(repoName)}/branches`,
      ),
    staleTime: INFREQUENT_STALE_TIME,
    enabled: !!repoName,
  });

  // Fetch paginated PR list with status filter
  const {
    data: prData,
    isLoading: prLoading,
    isError: prError,
    error: prErrorObj,
  } = useQuery({
    queryKey: [...queryKeys.prs.list(repoName, statusFilter), currentCursor ?? ''],
    queryFn: () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      if (currentCursor) {
        params.set('cursor', currentCursor);
      }
      params.set('limit', String(PR_PAGE_SIZE));
      const queryString = params.toString();
      return apiClient.get<PaginatedResponse<PrSummary>>(
        `/repos/${encodeURIComponent(repoName)}/prs${queryString ? `?${queryString}` : ''}`,
      );
    },
    staleTime: FREQUENT_STALE_TIME,
    enabled: !!repoName,
  });

  const hasNextPage = !!prData?.cursor;
  const hasPreviousPage = currentPage > 0;

  function handleStatusChange(newStatus: StatusFilter) {
    if (newStatus !== statusFilter) {
      setStatusFilter(newStatus);
      setCursors([]);
      setCurrentPage(0);
    }
  }

  function handleNextPage() {
    if (prData?.cursor) {
      setCursors((prev) => {
        const updated = [...prev];
        updated[currentPage] = prData.cursor!;
        return updated;
      });
      setCurrentPage((prev) => prev + 1);
    }
  }

  function handlePreviousPage() {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  }

  // Handle 403 - Access Denied
  if (repoError && repoErrorObj instanceof ApiError && repoErrorObj.status === 403) {
    return (
      <main id="main-content" className="max-w-5xl mx-auto px-4 py-6">
        <div className="text-center py-12" role="alert">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Access Denied
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            You do not have access to this repository.
          </p>
          <Link
            to="/repos"
            className="mt-4 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            Back to Repositories
          </Link>
        </div>
      </main>
    );
  }

  // Handle 404 - Not Found
  if (repoError && repoErrorObj instanceof ApiError && repoErrorObj.status === 404) {
    return (
      <main id="main-content" className="max-w-5xl mx-auto px-4 py-6">
        <div className="text-center py-12" role="alert">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Repository Not Found
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            The repository you are looking for does not exist.
          </p>
          <Link
            to="/repos"
            className="mt-4 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            Back to Repositories
          </Link>
        </div>
      </main>
    );
  }

  // Handle other repo errors
  if (repoError) {
    return (
      <main id="main-content" className="max-w-5xl mx-auto px-4 py-6">
        <div
          role="alert"
          className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
        >
          <p className="font-medium">Failed to load repository</p>
          <p className="text-sm mt-1">
            {repoErrorObj instanceof Error ? repoErrorObj.message : 'An unexpected error occurred'}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="max-w-5xl mx-auto px-4 py-6">
      {/* Project Breadcrumb — shown when accessed via /projects/:projectKey/repos/:repoName */}
      {projectKey && (
        <ProjectBreadcrumb projectKey={projectKey} repoName={repoName} />
      )}

      {/* Repository Info Section */}
      {repoLoading ? (
        <RepoInfoSkeleton />
      ) : (
        repo && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {repo.name}
            </h1>
            {repo.description && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {repo.description}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {repo.defaultBranch && (
                <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                  Default: {repo.defaultBranch}
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Updated {formatRelativeTime(repo.lastModifiedAt)}
              </span>
            </div>

            {/* Branches */}
            {branches?.items && branches.items.length > 0 && (
              <div className="mt-4">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Branches
                </h2>
                <div className="flex flex-wrap gap-2">
                  {branches.items.map((branch) => (
                    <span
                      key={branch.name}
                      className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                    >
                      {branch.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      )}

      {/* Pull Requests Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Pull Requests
          </h2>
          <Link
            to={projectKey
              ? `/projects/${encodeURIComponent(projectKey)}/repos/${encodeURIComponent(repoName)}/prs/new`
              : `/repos/${encodeURIComponent(repoName)}/prs/new`}
            className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            New Pull Request
          </Link>
        </div>

        {/* Status Filter */}
        <div className="mb-4" role="group" aria-label="Filter pull requests by status">
          {(['open', 'closed', 'all'] as const).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              aria-pressed={statusFilter === status}
              className={`px-4 py-2 text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:z-10 ${
                status === 'open'
                  ? 'rounded-l-lg'
                  : status === 'all'
                    ? 'rounded-r-lg'
                    : ''
              } ${
                statusFilter === status
                  ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* PR List */}
        {prLoading && <PrListSkeleton />}

        {prError && (
          <div
            role="alert"
            className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
          >
            <p className="font-medium">Failed to load pull requests</p>
            <p className="text-sm mt-1">
              {prErrorObj instanceof Error ? prErrorObj.message : 'An unexpected error occurred'}
            </p>
          </div>
        )}

        {!prLoading && !prError && prData && (
          <>
            {prData.items.length === 0 ? (
              <div className="text-center py-12 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-gray-500 dark:text-gray-400">
                  No {statusFilter === 'all' ? '' : statusFilter} pull requests found.
                </p>
              </div>
            ) : (
              <ul className="space-y-3" aria-label="Pull request list">
                {prData.items.map((pr) => (
                  <PrEntry key={pr.prId} pr={pr} repoName={repoName} projectKey={projectKey} />
                ))}
              </ul>
            )}

            {/* Pagination Controls */}
            {(hasPreviousPage || hasNextPage) && (
              <nav
                aria-label="Pull request list pagination"
                className="mt-6 flex items-center justify-between"
              >
                <button
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                  aria-label="Previous page"
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage + 1}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  aria-label="Next page"
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}
      </section>
    </main>
  );
}
